import { useState, useCallback } from "react";
import { useIntl } from "react-intl";
import Project from "../../project/models/Project";
import { FeedbackToastProps } from "../components/FeedbackToast";
import { pushToDataLayer } from "../utils/tracking";
import { buildExportProposalDataLayerEvent } from "../utils/projectExportTracking";

/**
 * Downloads installation GeoJSON for a project from the API (no request body; GTM uses same `exportProposal` event as PPT).
 */
const useProjectGeoJsonExport = (
  project: Project | undefined,
  onShowToast?: (toast: FeedbackToastProps) => void
) => {
  const [isExportingGeoJson, setIsExportingGeoJson] = useState(false);
  const intl = useIntl();

  const exportInstallationGeoJson = useCallback(async () => {
    if (!project) return;

    const showFail = () => {
      onShowToast?.({
        show: true,
        success: false,
        text: intl.formatMessage(
          {
            id: "general.exportGeoJsonFailed",
            defaultMessage:
              "Could not export installation data for {projectName}.",
          },
          { projectName: project.name }
        ),
      });
    };

    setIsExportingGeoJson(true);
    try {
      const response = await fetch(
        `/api/projects/${project.id}/export-geojson`,
        { method: "GET" }
      );

      if (!response.ok) {
        showFail();
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `Installation ${project.name}.json`);
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);

      onShowToast?.({
        show: true,
        success: true,
        text: intl.formatMessage(
          {
            id: "general.exportGeoJsonSuccess",
            defaultMessage:
              "Installation data exported for {projectName}.",
          },
          { projectName: project.name }
        ),
      });

      pushToDataLayer(
        buildExportProposalDataLayerEvent(project, {
          documentname: `Installation ${project.name}.json`,
          documenttype: "json",
        })
      );
    } catch (error) {
      console.error("GeoJSON export failed:", error);
      showFail();
    } finally {
      setIsExportingGeoJson(false);
    }
  }, [project, onShowToast, intl]);

  return {
    isExportingGeoJson,
    exportInstallationGeoJson,
  };
};

export default useProjectGeoJsonExport;
